import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { money } from "@/lib/money";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, FilePdf } from "@phosphor-icons/react";
import NewProposalModal from "@/components/NewProposalModal";
import { PageHeader, StatCard, DraftStatusPill, REVIEWABLE, shortDate } from "./shared";

export default function SalesProposals() {
  const { autoSend } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const load = () => api.get("/sales/proposals").then((r) => setRows(Array.isArray(r.data) ? r.data : []));
  useEffect(() => { load(); }, []);

  const withdraw = async (p) => {
    if (!window.confirm(`Withdraw the proposal for "${p.company_name}"? It's removed from the admin review queue.`)) return;
    setBusyId(p.id);
    try { await api.delete(`/sales/proposals/${p.id}`); toast.success("Proposal withdrawn."); load(); }
    catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setBusyId(null); }
  };
  const count = (s) => rows.filter((r) => r.status === s).length;

  return (
    <div data-testid="sales-proposals">
      <PageHeader eyebrow="Sell" title="My Proposals" blurb={autoSend ? "Draft a proposal for a prospective client — it's emailed to them with the e-signature link as soon as you send it, and the office is notified. Accepted proposals become projects credited to you."
                                                       : "Draft a proposal for a prospective client. An admin reviews the terms, then it's sent with the e-signature link. Accepted proposals become projects credited to you."}
        action={<button onClick={() => setOpenNew(true)} data-testid="sales-new-proposal-btn" className="h-11 px-4 bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-md flex items-center gap-2"><Plus size={18} weight="bold" /> New proposal</button>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard label={autoSend ? "Awaiting approval (older)" : "Awaiting admin approval"} value={count("pending_review")} tone={count("pending_review") ? "amber" : "slate"} testId="sp-stat-pending" />
        <StatCard label="Changes requested" value={count("changes_requested")} tone={count("changes_requested") ? "amber" : "slate"} testId="sp-stat-changes" />
        <StatCard label="Out with clients" value={rows.filter((r) => r.status === "sent" && !r.is_expired).length} testId="sp-stat-sent" />
        <StatCard label="Won" value={count("accepted") + count("converted")} tone="emerald" testId="sp-stat-won" />
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Client</th>
              <th className="text-left px-4 py-3">Contact</th>
              <th className="text-left px-4 py-3">Bill rate</th>
              <th className="text-left px-4 py-3">Submitted</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No proposals yet — click <b>New proposal</b> to draft your first one.</td></tr>}
            {rows.map((p) => {
              const reviewable = REVIEWABLE.includes(p.status);
              return (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`sales-proposal-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium">{p.company_name}<div className="text-xs text-slate-500">{p.location}</div></td>
                  <td className="px-4 py-3 text-slate-600">{p.contact_name}<div className="text-xs text-slate-500">{p.contact_email}</div></td>
                  <td className="px-4 py-3 font-mono">{money(p.hourly_rate)}/hr{p.per_diem_applies ? <span className="text-xs text-slate-500 font-sans"> + PD</span> : null}</td>
                  <td className="px-4 py-3 text-slate-600">{shortDate(p.submitted_at || p.created_at)}</td>
                  <td className="px-4 py-3">
                    <DraftStatusPill doc={p} />
                    {p.status === "changes_requested" && p.review_note && <div className="text-xs text-orange-800 italic mt-1 max-w-[260px] truncate" title={p.review_note}>“{p.review_note}”</div>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Link to={`/sales/proposals/${p.id}`} data-testid={`sales-proposal-open-${p.id}`} className="text-teal-700 hover:text-teal-900 font-semibold">Open</Link>
                    <a href={`${API_BASE}/sales/proposals/${p.id}/pdf`} target="_blank" rel="noreferrer" title="Preview PDF" data-testid={`sales-proposal-pdf-${p.id}`}
                       className="ml-3 text-slate-500 hover:text-teal-700 align-middle inline-block"><FilePdf size={16} weight="bold" /></a>
                    {reviewable && (
                      <>
                        <button onClick={() => setEditing(p)} disabled={busyId === p.id} data-testid={`sales-proposal-edit-${p.id}`} title="Edit & resubmit"
                                className="ml-3 text-slate-500 hover:text-teal-700 align-middle disabled:opacity-40"><PencilSimple size={16} weight="bold" /></button>
                        <button onClick={() => withdraw(p)} disabled={busyId === p.id} data-testid={`sales-proposal-withdraw-${p.id}`} title="Withdraw"
                                className="ml-3 text-slate-400 hover:text-rose-600 align-middle disabled:opacity-40"><Trash size={16} weight="bold" /></button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {openNew && <NewProposalModal mode="sales" autoSend={autoSend} onClose={() => setOpenNew(false)} onCreated={load} />}
      {editing && <NewProposalModal mode="sales" autoSend={autoSend} initial={editing} onClose={() => setEditing(null)} onCreated={load} />}
    </div>
  );
}
